<template>
  <v-container fluid>
    <div class="d-flex flex-column employee-card-path-to-readiness">
      <EmployeePageHeader
        v-if="isNewLayoutEnabled"
        :employeeData="employeeData"
        :loading="loading"
        @employee-updated="handleEmployeeUpdate"
        @refresh-employee-data="handleRefreshEmployeeData"
        @recalc-readiness="onEmployeeReadiness"
      />
      <div>
        <SharedTableSkeleton
          v-if="showSkeletonLoading"
          :row-count="5"
          :column-count="1"
          column-template="1fr"
        />
        <NoRecordFound
          v-else-if="chatterData.length === 0"
          title="Employee Chatter"
          message="Employee Chatter"
          btnTitle=""
          class="chatter-not-found"
        />
        <ActivityStream
          v-else
          class="mx-4"
          :dataStream="chatterData"
          :show-load-more-button="totalChatterData > chatterData.length"
          @onLoadMore="getEmployeesChatter"
          :dynamicOffset="85"
        />
      </div>
    </div>
  </v-container>
</template>
<script>
import ActivityStream from "@components/Chatter/ActivityStream.vue";
import { getEmployeeChatter } from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import EmployeePageHeader from "@components/common/EmployeePageHeader.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";

export default {
  name: "EmployeeChatterView",
  components: {
    ActivityStream,
    NoRecordFound,
    EmployeePageHeader,
    SharedTableSkeleton,
  },
  data() {
    return {
      chatterData: [],
      totalChatterData: [],
      accountID: "",
      employeeData: null,
      loading: {
        readinessBar: false,
      },
      chatterLoading: true,
      // MDP-560 — gated behind idea-MDP-560 (consistent skeleton loading).
      skeletonLoadingEnabled: false,
    };
  },
  computed: {
    isNewLayoutEnabled() {
      return this.$store.getters["featureFlags/isNewDesign"];
    },
    showSkeletonLoading() {
      return this.chatterLoading && this.chatterData.length === 0 && this.skeletonLoadingEnabled;
    },
  },
  watch: {
    "$route.params.id": {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.$nextTick(() => {
            this.handleRefreshEmployeeData();
          });
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    // MDP-560 — gate skeleton loading behind the consistent-skeleton feature flag.
    try {
      this.skeletonLoadingEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
    } catch (error) {
      this.skeletonLoadingEnabled = false;
    }
    if (!this.skeletonLoadingEnabled) {
      this.$loader.show();
    }
    await this.getEmployeeData();
    await this.getEmployeesChatter();
    if (!this.skeletonLoadingEnabled) {
      this.$loader.hide();
    }
  },
  methods: {
    async getEmployeeData() {
      try {
        // Get employee data from store (should be loaded by sidebar)
        const employeeData = this.$store.getters["employeeStore/currentEmployee"];
        if (employeeData) {
          this.employeeData = employeeData;
        }
      } catch (e) {
        console.error("Failed to load employee data", e);
      }
    },
    async handleRefreshEmployeeData() {
      await this.getEmployeeData();
    },
    async getEmployeesChatter() {
      const empId = this.$route.params.id;
      const page = Math.ceil(this.chatterData.length / 10);
      this.chatterLoading = true;
      if (!this.skeletonLoadingEnabled) {
        this.$loader.show();
      }
      try {
        const { data } = await getEmployeeChatter(this.accountID, empId, page + 1);
        if (data && data.count) {
          this.totalChatterData = data.count;
        }
        if (data && data.results) {
          const chatterItems = transformChatterData(data.results);
          this.chatterData.push(...chatterItems);
        }
      } catch (error) {
        console.error("Failed to load employee chatter", error);
      } finally {
        this.chatterLoading = false;
        if (!this.skeletonLoadingEnabled) {
          this.$loader.hide();
        }
      }
    },
    handleEmployeeUpdate() {
      this.employeeData = this.$store.getters["employeeStore/currentEmployee"];
    },
    async onEmployeeReadiness() {
      // Readiness calculation is now handled by the sidebar component
      // Employee data will be updated automatically in the store
      const empId = this.$route.params.id;
      if (empId) {
        // Emit event to sidebar to trigger readiness recalculation
        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);

        // Update local employee data from store
        this.employeeData = this.$store.getters["employeeStore/currentEmployee"];
      }
    },
  },
};
</script>
<style>
.custom-chatter-view {
  margin-top: -35px;
}
.chatter-not-found .badge {
  display: none;
}
.chatter-not-found button {
  display: none;
}
</style>
